import { useEffect, useState } from "react";
import { Smartphone, ShieldAlert, Apple, Share, SquarePlus, CheckCircle2, MonitorSmartphone } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PublicShell } from "@/components/PublicLayout";

const Step = ({ n, children }) => (
  <li className="flex gap-3 text-sm"><span className="h-6 w-6 shrink-0 rounded-full bg-[#2412B5]/10 text-[#2412B5] text-xs font-semibold flex items-center justify-center">{n}</span><span className="pt-0.5">{children}</span></li>
);

function useInstallPrompt() {
  const [evt, setEvt] = useState(null);
  useEffect(() => {
    const h = (e) => { e.preventDefault(); setEvt(e); };
    window.addEventListener("beforeinstallprompt", h);
    return () => window.removeEventListener("beforeinstallprompt", h);
  }, []);
  return evt;
}

export default function DownloadApp() {
  const prompt = useInstallPrompt();
  const [installed, setInstalled] = useState(() => window.matchMedia?.("(display-mode: standalone)").matches);
  useEffect(() => { const h = () => setInstalled(true); window.addEventListener("appinstalled", h); return () => window.removeEventListener("appinstalled", h); }, []);
  const install = async () => {
    if (prompt) { prompt.prompt(); const r = await prompt.userChoice.catch(() => null); if (r?.outcome === "accepted") setInstalled(true); return; }
    toast.info("Open this page in Chrome, tap the ⋮ menu (top-right) and choose “Install app” or “Add to Home screen”.", { duration: 9000 });
  };
  return (
    <PublicShell>
    <div className="mx-auto max-w-5xl px-5 sm:px-8 py-14" data-testid="download-page">
      <p className="eyebrow">Get the app</p>
      <h1 className="mt-3 text-4xl sm:text-5xl font-semibold tracking-tight text-[#17232E]">Nirogyam on your phone</h1>
      <p className="mt-4 max-w-2xl text-base muted">Consult doctors, order medicines, book lab tests and keep your family's health records – all from your home screen.</p>

      <div className="mt-10 grid lg:grid-cols-2 gap-6">
        <section className="surface p-6" data-testid="download-android">
          <div className="flex items-center gap-3"><span className="h-11 w-11 rounded-2xl bg-[#086B2D]/10 flex items-center justify-center"><Smartphone className="h-5 w-5 text-[#086B2D]" /></span>
            <div><h2 className="font-semibold text-lg">Android</h2><p className="text-xs muted">Installs from Chrome in seconds · no APK file needed</p></div></div>
          <Button className="w-full rounded-full h-12 mt-5" onClick={install} disabled={installed} data-testid="pwa-install-btn">
            <MonitorSmartphone className="h-4 w-4 mr-2" />{installed ? "Nirogyam is installed" : "Install directly from your browser"}
          </Button>
          <div className="mt-4 rounded-xl border border-[#F5C26B] bg-[#FFF8EB] p-4 text-sm text-[#7A4A00]" role="alert" data-testid="install-caution">
            <p className="font-semibold flex items-center gap-1.5"><ShieldAlert className="h-4 w-4" />Install only from this website</p>
            <p className="mt-1">For your device's safety, install the Nirogyam app only from <b>nirogyam.net</b> using the button above. Never install a Nirogyam app or APK file received on WhatsApp, Telegram, SMS, email or from any other website – it may be fake or harmful.</p>
          </div>
          <ol className="mt-5 space-y-3" data-testid="android-steps">
            <Step n={1}>Open <b>nirogyam.net/download</b> in <b>Google Chrome</b> and tap <b>Install directly from your browser</b>.</Step>
            <Step n={2}>Tap <b>Install</b> in the pop-up. If no pop-up appears, tap Chrome's <b>⋮</b> menu (top-right) and choose <b>“Install app”</b> or <b>“Add to Home screen”</b>.</Step>
            <Step n={3}>Open <b>Nirogyam</b> from your home screen and sign in.</Step>
          </ol>
        </section>

        <section className="surface p-6" data-testid="download-ios">
          <div className="flex items-center gap-3"><span className="h-11 w-11 rounded-2xl bg-[#2412B5]/10 flex items-center justify-center"><Apple className="h-5 w-5 text-[#2412B5]" /></span>
            <div><h2 className="font-semibold text-lg">iPhone & iPad</h2><p className="text-xs muted">Add Nirogyam to your Home Screen – no App Store needed</p></div></div>
          <ol className="mt-5 space-y-3" data-testid="ios-steps">
            <Step n={1}>Open <b>nirogyam.net</b> in <b>Safari</b>.</Step>
            <Step n={2}>Tap the <b>Share</b> button <Share className="inline h-4 w-4 -mt-0.5" /> at the bottom of the screen (top-right on iPad).</Step>
            <Step n={3}>Scroll down and tap <b>Add to Home Screen</b> <SquarePlus className="inline h-4 w-4 -mt-0.5" />.</Step>
            <Step n={4}>Keep the name <b>Nirogyam</b> and tap <b>Add</b>. Open it from your Home Screen – it runs full-screen like an app.</Step>
          </ol>
          <ul className="mt-6 space-y-2 text-sm muted">
            {["Same account and records as the website", "Video consultations, chat and reminders", "Updates automatically – nothing to reinstall"].map((t) => (
              <li key={t} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[#086B2D]" />{t}</li>))}
          </ul>
        </section>
      </div>
    </div>
    </PublicShell>
  );
}
